import { useEffect, useState, useCallback } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Badge, Card, ErrorState } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select } from "../components/ui/Fields";
import { UploadButton } from "../components/ui/UploadButton";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

type MediaType = "svga" | "video" | "image";
interface Splash {
  enabled: boolean; status: "draft" | "published"; mediaType: MediaType; mediaUrl: string; completion: "complete" | "fixed";
  durationMs: number; maxDurationMs: number; skippable: boolean; fallbackImageUrl: string; version: number;
}
const ACCEPT: Record<MediaType, string> = { svga: ".svga", video: "video/mp4,video/webm", image: "image/png,image/jpeg,image/webp" };
const HINT: Record<MediaType, string> = {
  svga: "An .svga animation, up to 10 MB. Plays once, full screen, keeping its shape.",
  video: "An MP4 or WebM video, up to 30 MB. Plays once, full screen, keeping its shape.",
  image: "A PNG, JPG or WebP picture, up to 8 MB. Shown full screen for the time below.",
};

/** Round 60 - Splash Screen: a configurable full-screen intro (SVGA / video / image). The app falls back to its built-in splash whenever this is off, a draft, or cannot be played. */
export function SplashPage() {
  const { showToast } = useToast();
  const [s, setS] = useState<Splash | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    setError(null);
    apiRequest<Splash>("/admin/splash").then(setS).catch((e) => setError(e instanceof ApiError ? e.message : "could not load"));
  }, []);
  useEffect(load, [load]);

  async function save(next: Splash) {
    setSaving(true);
    try {
      setS(await apiRequest<Splash>("/admin/splash", { method: "PATCH", body: next }));
      showToast(next.enabled && next.status === "published" ? "Saved - live for the next app start" : "Saved", "success");
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "could not save", "error");
    } finally {
      setSaving(false);
    }
  }
  const set = (patch: Partial<Splash>) => setS((p) => (p ? { ...p, ...patch } : p));

  return (
    <AppShell title="Splash Screen">
      {error ? <ErrorState message={error} onRetry={load} /> : !s ? <p className="text-sm text-ink-muted">Loading…</p> : (
        <div className="max-w-3xl space-y-5">
          <Card className="space-y-4 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">Full-screen splash</h2>
                <p className="mt-1 text-xs text-ink-faint">Shown when the app starts. It never delays the app: the file is downloaded in the background and played from the next start; until then (or if anything fails) the built-in splash is used.</p>
              </div>
              <Badge tone={s.enabled && s.status === "published" ? "success" : "neutral"}>{s.enabled && s.status === "published" ? "LIVE" : s.enabled ? "DRAFT" : "OFF"}</Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Select label="Splash" value={s.enabled ? "on" : "off"} onChange={(e) => set({ enabled: e.target.value === "on" })}>
                <option value="off">Disabled (use the built-in splash)</option>
                <option value="on">Enabled</option>
              </Select>
              <Select label="Publication status" value={s.status} onChange={(e) => set({ status: e.target.value as Splash["status"] })}>
                <option value="draft">Draft (not shown to users)</option>
                <option value="published">Published</option>
              </Select>
              <Select label="Media type" value={s.mediaType} onChange={(e) => set({ mediaType: e.target.value as MediaType, completion: e.target.value === "image" ? "fixed" : s.completion })}>
                <option value="image">Image</option>
                <option value="video">Video (MP4 / WebM)</option>
                <option value="svga">SVGA animation</option>
              </Select>
              <Select label="Ends" value={s.completion} disabled={s.mediaType === "image"} onChange={(e) => set({ completion: e.target.value as Splash["completion"] })}>
                <option value="complete">When the animation / video finishes</option>
                <option value="fixed">After a fixed time</option>
              </Select>
            </div>
            <div className="space-y-1">
              <Input label="Media file address (https)" value={s.mediaUrl} onChange={(e) => set({ mediaUrl: e.target.value })} placeholder="https://…" hint={HINT[s.mediaType]} />
              <UploadButton kind="splash" accept={ACCEPT[s.mediaType]} onUploaded={(u) => set({ mediaUrl: u })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Input type="number" label="Fixed time (ms)" value={s.durationMs} min={500} max={15000} onChange={(e) => set({ durationMs: Number(e.target.value) })} hint="For images, or when 'after a fixed time'." />
              <Input type="number" label="Never longer than (ms)" value={s.maxDurationMs} min={500} max={15000} onChange={(e) => set({ maxDurationMs: Number(e.target.value) })} hint="Safety limit; the app moves on at this point." />
              <Select label="Tap to skip" value={s.skippable ? "yes" : "no"} onChange={(e) => set({ skippable: e.target.value === "yes" })}>
                <option value="yes">Allowed</option>
                <option value="no">Not allowed</option>
              </Select>
            </div>
            <div className="space-y-1">
              <Input label="Fallback image (optional)" value={s.fallbackImageUrl} onChange={(e) => set({ fallbackImageUrl: e.target.value })} placeholder="https://… (PNG / JPG / WebP)" hint="Shown instead if the video or animation cannot be played on a device. Empty = the built-in splash." />
              <UploadButton kind="splash" accept={ACCEPT.image} onUploaded={(u) => set({ fallbackImageUrl: u })} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-ink-faint">Version {s.version} - every save creates a new version, so devices pick up the change on their next start.</span>
              <Button disabled={saving} onClick={() => save(s)}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          </Card>
          <Card className="space-y-2 p-5 text-xs text-ink-muted">
            <h2 className="text-sm font-semibold text-ink">Good to know</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>Design for a tall phone screen (for example 1080×2400). The picture is shown whole - never cropped or stretched - on a matching background, so other screen shapes get bars rather than distortion.</li>
              <li>Keep it short (2–4 seconds is typical) and light; large files take longer to reach every device.</li>
              <li>Only your own storage and approved hosts are accepted. Nothing plays unless it is Enabled <b>and</b> Published.</li>
              <li>A device shows the previous splash (or the built-in one) on the very first start after a change, because the new file is fetched in the background.</li>
            </ul>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
